/**
 * API client functions for procedure catalog operations
 * Procedures = Diagnostic services like Physical Exams, X-rays, Ultrasound, etc.
 * (NOT lab tests - those are in labsCatalogApi.ts)
 */

import { API_BASE_URL } from '../config/api';

export interface ProcedureCatalogItem {
  id: string;
  practice_id?: string | null;
  based_on_standard_id?: string | null;
  name: string;
  procedure_type: string; // EXAM, IMAGING, VITALS, OTHER
  description?: string | null;
  default_duration_minutes?: number | null;
  requires_equipment: boolean;
  equipment_notes?: string | null;
  notes?: string | null;
  is_active: boolean;
  is_standard: boolean;
  is_stock_tracked?: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface ProcedureCatalogCreate {
  name: string;
  procedure_type?: string;
  description?: string | null;
  default_duration_minutes?: number | null;
  requires_equipment?: boolean;
  equipment_notes?: string | null;
  notes?: string | null;
  display_order?: number;
  based_on_standard_id?: string | null;
}

export interface ProcedureCatalogUpdate {
  name?: string;
  procedure_type?: string;
  description?: string | null;
  default_duration_minutes?: number | null;
  requires_equipment?: boolean;
  equipment_notes?: string | null;
  notes?: string | null;
  display_order?: number;
  is_active?: boolean;
  is_stock_tracked?: boolean;
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
}

/**
 * Get all procedure catalog items for a practice
 */
export async function getProcedureCatalog(
  practiceId: string,
  search?: string,
  procedureType?: string,
  activeOnly: boolean = true
): Promise<ProcedureCatalogItem[]> {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  if (procedureType) params.append('procedure_type', procedureType);
  params.append('active_only', activeOnly.toString());

  const url = `${API_BASE_URL}/api/v1/practices/${practiceId}/procedures?${params.toString()}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch procedure catalog items' }));
    throw new Error(error.detail || 'Failed to fetch procedure catalog items');
  }

  return response.json();
}

/**
 * Get a single procedure catalog item
 */
export async function getProcedureCatalogItem(
  practiceId: string,
  itemId: string
): Promise<ProcedureCatalogItem> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/procedures/${itemId}`,
    {
      method: 'GET',
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch procedure catalog item' }));
    throw new Error(error.detail || 'Failed to fetch procedure catalog item');
  }

  return response.json();
}

/**
 * Create a new procedure catalog item
 */
export async function createProcedureCatalogItem(
  practiceId: string,
  data: ProcedureCatalogCreate
): Promise<ProcedureCatalogItem> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/procedures`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to create procedure catalog item' }));
    throw new Error(error.detail || 'Failed to create procedure catalog item');
  }

  return response.json();
}

/**
 * Update a procedure catalog item
 */
export async function updateProcedureCatalogItem(
  practiceId: string,
  itemId: string,
  data: ProcedureCatalogUpdate
): Promise<ProcedureCatalogItem> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/procedures/${itemId}`,
    {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to update procedure catalog item' }));
    throw new Error(error.detail || 'Failed to update procedure catalog item');
  }

  return response.json();
}

/**
 * Soft delete a procedure catalog item (set is_active=false)
 */
export async function deleteProcedureCatalogItem(
  practiceId: string,
  itemId: string
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/procedures/${itemId}`,
    {
      method: 'DELETE',
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete procedure catalog item' }));
    throw new Error(error.detail || 'Failed to delete procedure catalog item');
  }
}

/**
 * Hard delete a procedure catalog item (permanently removes it and linked billing item)
 */
export async function hardDeleteProcedureCatalogItem(
  practiceId: string,
  itemId: string
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/procedures/${itemId}/hard`,
    {
      method: 'DELETE',
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete procedure catalog item' }));
    throw new Error(error.detail || 'Failed to delete procedure catalog item');
  }
}

